/**
 * Versioned full-leaderboard cache for league pages.
 * Invalidated when leagues/{id}.ranksLastUpdated or competingCount changes.
 */
import { getDocs, type DocumentData, type Firestore, type QuerySnapshot } from 'firebase/firestore';
import type { Dict } from '@/helpers';
import { toDict } from '@/helpers';
import { getCompetingCount } from '@/helpers/leagues/leagueMemberSync';
import type League from '@/models/league';
import { queryLeagueLeaderboardMembers } from '@/models/league';
import type { leagueMember } from '@/models/user';

const SESSION_KEY_PREFIX = 'tt-leaderboard-';
const MEMBERS_PER_PAGE = 50;

export type LeaderboardCacheVersion = {
  competingCount: number;
  ranksLastUpdatedMs: number;
};

export type LeaderboardCacheEntry = LeaderboardCacheVersion & {
  membersByUid: Dict<leagueMember>;
  memberIdsInOrder: string[];
  fetchedAt: number;
};

const memoryCache = new Map<string, LeaderboardCacheEntry>();
const inflightFetches = new Map<string, Promise<LeaderboardCacheEntry>>();

function cacheStorageKey(leagueId: string, version: LeaderboardCacheVersion): string {
  return `${SESSION_KEY_PREFIX}${leagueId}:${version.competingCount}:${version.ranksLastUpdatedMs}`;
}

export function getLeaderboardCacheVersion(league: League): LeaderboardCacheVersion {
  return {
    competingCount: getCompetingCount(league),
    ranksLastUpdatedMs: league.ranksLastUpdated?.toMillis?.() ?? 0,
  };
}

export function isLeaderboardCacheValid(
  cached: LeaderboardCacheVersion,
  league: League,
): boolean {
  const current = getLeaderboardCacheVersion(league);
  return (
    cached.competingCount === current.competingCount &&
    cached.ranksLastUpdatedMs === current.ranksLastUpdatedMs
  );
}

function cacheVersionsMatch(
  cached: LeaderboardCacheVersion,
  version: LeaderboardCacheVersion,
): boolean {
  return (
    cached.competingCount === version.competingCount &&
    cached.ranksLastUpdatedMs === version.ranksLastUpdatedMs
  );
}

export function sliceLeaderboardPage(
  entry: LeaderboardCacheEntry,
  pageIndex: number,
  pageSize: number,
): { memberIds: string[]; members: Dict<leagueMember> } {
  const start = pageIndex * pageSize;
  const memberIds = entry.memberIdsInOrder.slice(start, start + pageSize);
  const members: Dict<leagueMember> = {};
  for (const uid of memberIds) {
    members[uid] = entry.membersByUid[uid];
  }
  return { memberIds, members };
}

function readSessionCache(
  leagueId: string,
  version: LeaderboardCacheVersion,
): LeaderboardCacheEntry | undefined {
  try {
    const raw = sessionStorage.getItem(cacheStorageKey(leagueId, version));
    if (!raw) return undefined;
    const parsed = JSON.parse(raw) as LeaderboardCacheEntry;
    if (!cacheVersionsMatch(parsed, version)) {
      return undefined;
    }
    return parsed;
  } catch {
    return undefined;
  }
}

function writeSessionCache(leagueId: string, entry: LeaderboardCacheEntry): void {
  try {
    sessionStorage.setItem(
      cacheStorageKey(leagueId, entry),
      JSON.stringify(entry),
    );
  } catch {
    // sessionStorage full or unavailable
  }
}

export function getCachedLeaderboard(
  leagueId: string,
  version: LeaderboardCacheVersion,
): LeaderboardCacheEntry | undefined {
  const mem = memoryCache.get(leagueId);
  if (mem && cacheVersionsMatch(mem, version)) {
    return mem;
  }

  const session = readSessionCache(leagueId, version);
  if (session) {
    memoryCache.set(leagueId, session);
    return session;
  }
  return undefined;
}

export function setCachedLeaderboard(leagueId: string, entry: LeaderboardCacheEntry): void {
  memoryCache.set(leagueId, entry);
  writeSessionCache(leagueId, entry);
}

function entryFromSnapshot(
  snapshot: QuerySnapshot<DocumentData>,
  version: LeaderboardCacheVersion,
): LeaderboardCacheEntry {
  return {
    ...version,
    membersByUid: toDict(snapshot) as Dict<leagueMember>,
    memberIdsInOrder: snapshot.docs.map((doc) => doc.id),
    fetchedAt: Date.now(),
  };
}

export async function ensureLeaderboardCache(
  db: Firestore,
  league: League & { id: string },
): Promise<LeaderboardCacheEntry> {
  const version = getLeaderboardCacheVersion(league);
  const cached = getCachedLeaderboard(league.id, version);
  if (cached) {
    return cached;
  }

  const existing = inflightFetches.get(league.id);
  if (existing) {
    return existing;
  }

  const promise = getDocs(queryLeagueLeaderboardMembers(db, league.id))
    .then((snapshot) => {
      const entry = entryFromSnapshot(snapshot, version);
      setCachedLeaderboard(league.id, entry);
      return entry;
    })
    .catch((error) => {
      console.error('[ensureLeaderboardCache]', error);
      throw error;
    })
    .finally(() => {
      inflightFetches.delete(league.id);
    });

  inflightFetches.set(league.id, promise);
  return promise;
}

export async function prefetchLeagueLeaderboard(
  db: Firestore,
  league: League & { id: string },
): Promise<LeaderboardCacheEntry> {
  return ensureLeaderboardCache(db, league);
}

export { MEMBERS_PER_PAGE as LEADERBOARD_MEMBERS_PER_PAGE };
